/**
 * mcgraw-hill: the reference documents the AI desktop cites, as the backend
 * configures them (MCGRAW_HILL_REFERENCE_DOCUMENTS in mcgrawHillConfig.ts
 * there; keep the two in step). A `[[cite:<cite key>:…]]` marker names a
 * document by its cite key; the role colours its badge (client documents red,
 * Katzung's chapters among them; articles green).
 */
export type McGrawHillReferenceRole = 'client' | 'article';

/**
 * A McGraw Hill textbook a reference document is a chapter of: Katzung's
 * Basic & Clinical Pharmacology 16e or Harrison's Principles of Internal
 * Medicine 22e. Its viewer shows the book's contents (McGrawHillContents).
 */
export type McGrawHillReferenceBook = 'katzung' | 'harrisons';

export interface McGrawHillReferenceChapter {
  book: McGrawHillReferenceBook;
  chapter: number;
  title: string;
}

export interface McGrawHillReference {
  id: string;
  citeKey: string;
  role: McGrawHillReferenceRole;
  /** Set on a textbook's chapters only (not DiPiro's, nor the articles). */
  textbook?: McGrawHillReferenceChapter;
}

export const MCGRAW_HILL_REFERENCES: readonly McGrawHillReference[] = [
  // Harrison's Principles of Internal Medicine 22e, Chapter 265
  {
    id: '373e321b-dcba-4d28-8815-ec730f73f82d',
    citeKey: '265',
    role: 'client',
    textbook: {
      book: 'harrisons',
      chapter: 265,
      title: 'Heart Failure: Management',
    },
  },
  // Harrison's Principles of Internal Medicine 22e, Chapter 416
  {
    id: '466bbafe-5dd9-45fd-953b-be7befab34a5',
    citeKey: '416',
    role: 'client',
    textbook: {
      book: 'harrisons',
      chapter: 416,
      title: 'Diabetes Mellitus: Management and Therapies',
    },
  },
  // DiPiro's Pharmacotherapy 13e, Chapter 147 Sepsis and Septic Shock
  {
    id: 'b63487b8-0b28-4d1c-b3a9-1a4f3c2fb5f3',
    citeKey: 'D147',
    role: 'article',
  },
  // Lancet 2022, Impact of diabetes on the effects of sodium glucose co-tr
  {
    id: '79c032c6-940c-45e1-afac-bb186688fd6c',
    citeKey: 'S3',
    role: 'article',
  },
  // New England Journal of Medicine 2022, Empagliflozin in Patients with C
  {
    id: '95c0d35c-0aaa-448d-8bef-752e710deae2',
    citeKey: 'S4',
    role: 'article',
  },
  // JAMA Cardiology 2023, Optimization of Evidence-Based Heart Failure Med
  {
    id: '497dd93e-6538-44aa-802c-138519120304',
    citeKey: 'S5',
    role: 'article',
  },
  // Nature Medicine 2022, The SGLT2 inhibitor empagliflozin in patients ho
  {
    id: 'c4e729d3-5803-492c-bbee-a4ad3e740b14',
    citeKey: 'S6',
    role: 'article',
  },
  // Nature Medicine 2021, The SGLT2 inhibitor dapagliflozin in heart failu
  {
    id: '039a4088-5f24-4fd4-9517-2bbb3d89dd6f',
    citeKey: 'S7',
    role: 'article',
  },
  // Circulation 2020, Effect of Empagliflozin on the Clinical Stability of
  {
    id: 'df51bdfe-e291-4a20-b24c-fcae307acebe',
    citeKey: 'S8',
    role: 'article',
  },
  // Circulation 2019, Effects of Dapagliflozin on Symptoms, Function, and
  {
    id: '0cdf36a9-7cda-4d70-ba0f-692ca752be94',
    citeKey: 'S9',
    role: 'article',
  },
  // Diabetes Care 2015, Euglycemic Diabetic Ketoacidosis: A Potential Comp
  {
    id: '1a53563c-5c60-433b-be3c-8544ab238164',
    citeKey: 'S10',
    role: 'article',
  },
  // Circulation 2022, Iron Deficiency in Heart Failure and Effect of Dapag
  {
    id: '2ac26742-3a75-4876-86b5-f08fc4fc3c0c',
    citeKey: 'S11',
    role: 'article',
  },
  // Nature Medicine 2024, Finerenone in heart failure and chronic kidney d
  {
    id: 'de003e19-f9fa-48d4-9bc9-739e7c8065e7',
    citeKey: 'S12',
    role: 'article',
  },
  // Circulation 2023, Effects of Semaglutide on Symptoms, Function, and Qu
  {
    id: '14da6c8c-eb3c-4ff9-a497-5eb3125509c6',
    citeKey: 'S13',
    role: 'article',
  },
  // European Heart Journal 2023, Efficacy of ferric carboxymaltose in hear
  {
    id: '1faaf638-f505-41b5-a8b7-ba0863b68266',
    citeKey: 'S14',
    role: 'article',
  },
  // Katzung's Basic & Clinical Pharmacology 16e chapters (Katzung16-ChNN.pdf,
  // ORA-1247): the client's own textbook, so client documents like Harrison's.
  {
    id: '1002aef1-ed64-44f6-a066-7de6cb9338f8',
    citeKey: 'K1',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 1,
      title: 'Foundations of Pharmacology',
    },
  },
  {
    id: '5db7c2ae-02da-4e2b-b211-e0bb9e1f9ea7',
    citeKey: 'K2',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 2,
      title: 'Receptors, Signaling, and Dose–Response',
    },
  },
  {
    id: '52d91a0c-9b8d-4a2e-826f-013b21ada69e',
    citeKey: 'K3',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 3,
      title: 'Pharmacokinetics and Rational Dosing',
    },
  },
  {
    id: 'b57aeb73-d009-4db3-8c26-ea8513536654',
    citeKey: 'K4',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 4,
      title: 'Drug Biotransformation and Acetaminophen Toxicity',
    },
  },
  {
    id: '94bd0791-a484-4829-a954-916d8662cc13',
    citeKey: 'K5',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 5,
      title: 'Pharmacogenomics and Gene-Based Drug Safety',
    },
  },
  {
    id: 'da625b7b-f293-42c8-8a72-b6c3ac34acd4',
    citeKey: 'K6',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 6,
      title: 'Autonomic Nervous System: Neurotransmission and Pharmacology',
    },
  },
  {
    id: 'e3d9788d-6104-43c2-b544-edd7d7a8903a',
    citeKey: 'K7',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 7,
      title: 'Cholinergic Pharmacology',
    },
  },
  {
    id: 'de19ef25-59b0-43b5-81f4-c92c83d9a5b8',
    citeKey: 'K8',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 8,
      title: 'Antimuscarinic and Ganglion-Blocking Drugs',
    },
  },
  {
    id: 'fc1b924c-7d3f-4be0-b846-d45017a0c228',
    citeKey: 'K9',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 9,
      title: 'Sympathomimetic Drugs',
    },
  },
  {
    id: '049af2b8-b508-4f42-8dfc-b65ec882c1a4',
    citeKey: 'K10',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 10,
      title: 'Alpha- and Beta-Adrenergic Antagonists',
    },
  },
  {
    id: '00742fed-b7ec-45ad-aa40-778a5597debf',
    citeKey: 'K11',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 11,
      title: 'Antihypertensive Drugs and Clinical Management',
    },
  },
  {
    id: 'e6b074dd-a231-4fd8-84b9-57f892528273',
    citeKey: 'K12',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 12,
      title: 'Antianginal Drugs and Coronary Ischemia',
    },
  },
  {
    id: '67236560-ef5e-44a9-914e-15054e8cbce6',
    citeKey: 'K13',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 13,
      title: 'Heart Failure Drugs and Therapeutic Strategies',
    },
  },
  {
    id: 'cae5f92e-5688-4396-b8fb-daa9d05cdf94',
    citeKey: 'K14',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 14,
      title: 'Antiarrhythmic Drugs and Cardiac Electrophysiology',
    },
  },
  {
    id: '767068d2-a74a-43c4-b4a7-cf302a558378',
    citeKey: 'K15',
    role: 'client',
    textbook: {
      book: 'katzung',
      chapter: 15,
      title: 'Diuretics and Nephron Transport',
    },
  },
];

export const mcgrawHillReferenceById = (
  id: string | undefined
): McGrawHillReference | null =>
  MCGRAW_HILL_REFERENCES.find((reference) => reference.id === id) ?? null;

export const mcgrawHillReferenceByCiteKey = (
  key: string
): McGrawHillReference | null =>
  MCGRAW_HILL_REFERENCES.find(
    (reference) => reference.citeKey.toLowerCase() === key.toLowerCase()
  ) ?? null;

/** The scripted answers' documents, by cite key. */
export const mcgrawHillReferenceId = (citeKey: string): string => {
  const reference = mcgrawHillReferenceByCiteKey(citeKey);
  if (!reference) throw new Error(`Unknown McGraw Hill cite key ${citeKey}`);
  return reference.id;
};
